// Shifts - create + manage the master list of work shifts. Data lives in
// dtime.shifts (created by dTime); this page is dAdmin's admin surface.
//
// UX:
//   • Time format toggle (12h AM/PM default, 24h). Stored value is always
//     24h "HH:MM" - the toggle only changes how times are entered/shown.
//   • Start / End entered via three (12h: Hour + Minute + AM/PM) or two
//     (24h: Hour + Minute) native <select>s with fixed capped height so long
//     lists scroll. Minutes step by 5.
//   • Auto-fill End = Start + 8h on Start change - and keep re-suggesting
//     until the user edits End themselves (endTouched flag). Loading an
//     existing shift into the form preserves its End.
//   • Live Duration readout; 10h+ warns via dnAlert.confirm, 12h+ blocks.
//   • Table below: Shift Name (with "overnight" badge), Start/End (12h),
//     Duration, Edit + Delete actions.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import { FaPenSquare, FaTrashAlt, FaMoon } from "react-icons/fa";
import ScrollSelect from "./ScrollSelect";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Shifts.css";

const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1)); // 1..12

// ScrollSelect option lists ({ value, label }). value === label here since the
// picker's value/label are the same string; from12/from24 turn them into "HH:MM".
const HH24_OPTS = HOURS_24.map((h) => ({ value: h, label: h }));
const HH12_OPTS = HOURS_12.map((h) => ({ value: h, label: h }));
const MM_OPTS = MINUTES.map((m) => ({ value: m, label: m }));
const AMPM_OPTS = [{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }];

// Convert stored "HH:MM" → { h, m, ampm } for 12h display, or { h, m } for 24h.
function to12(hhmm) {
    if (!hhmm) return { h: "12", m: "00", ampm: "AM" };
    const [Hs, Ms] = hhmm.split(":");
    let H = parseInt(Hs, 10);
    const ampm = H >= 12 ? "PM" : "AM";
    H = H % 12; if (H === 0) H = 12;
    return { h: String(H), m: Ms, ampm };
}
function to24(hhmm) {
    if (!hhmm) return { h: "09", m: "00" };
    const [H, M] = hhmm.split(":");
    return { h: String(H).padStart(2, "0"), m: M };
}
function from12(h, m, ampm) {
    let H = parseInt(h, 10);
    if (ampm === "PM" && H !== 12) H += 12;
    if (ampm === "AM" && H === 12) H = 0;
    return `${String(H).padStart(2, "0")}:${m}`;
}
function from24(h, m) {
    return `${String(h).padStart(2, "0")}:${m}`;
}
function addMinutes(hhmm, mins) {
    const [H, M] = hhmm.split(":").map(Number);
    let total = H * 60 + M + mins;
    total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    const nh = Math.floor(total / 60), nm = total % 60;
    return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}
function durationMins(start, end) {
    if (!start || !end) return 0;
    const [Hs, Ms] = start.split(":").map(Number);
    const [He, Me] = end.split(":").map(Number);
    let d = (He * 60 + Me) - (Hs * 60 + Ms);
    if (d <= 0) d += 24 * 60;
    return d;
}
function fmtDuration(mins) {
    if (!mins) return "";
    const h = Math.floor(mins / 60), m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function fmtTime12(hhmm) {
    if (!hhmm) return "-";
    const { h, m, ampm } = to12(hhmm);
    return `${h}:${m} ${ampm}`;
}

function Shifts({ navSize }) {
    const [is12h, setIs12h] = useState(true);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState("");
    const [start, setStart] = useState("09:00");
    const [end, setEnd] = useState("17:00");
    const [endTouched, setEndTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchLogged();
        fetchList();
    }, []);

    const fetchLogged = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login");
            }
        } catch (err) { console.error("logined_employee:", err); }
    };

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/shift/list", { credentials: "include" });
            const data = await res.json();
            setList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("shift list:", err);
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    // Auto-suggest End whenever Start changes AND user hasn't touched End yet.
    const handleStartChange = (nextStart) => {
        setStart(nextStart);
        if (!endTouched) setEnd(addMinutes(nextStart, 8 * 60));
    };
    const handleEndChange = (nextEnd) => {
        setEnd(nextEnd);
        setEndTouched(true);
    };

    const durMin = useMemo(() => durationMins(start, end), [start, end]);
    const overnight = useMemo(() => {
        if (!start || !end) return false;
        const [Hs, Ms] = start.split(":").map(Number);
        const [He, Me] = end.split(":").map(Number);
        return He * 60 + Me <= Hs * 60 + Ms;
    }, [start, end]);

    const resetForm = () => {
        setEditingId(null);
        setName("");
        setStart("09:00");
        setEnd("17:00");
        setEndTouched(false);
    };

    const loadIntoForm = (row) => {
        setEditingId(row.shift_id);
        setName(row.shift_name);
        setStart(row.start_time);
        setEnd(row.end_time);
        setEndTouched(true); // preserve loaded end
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) { dnAlert.warning("Required", "Shift name is required."); return; }
        if (start === end) { dnAlert.warning("Invalid", "Start and end time cannot be the same."); return; }
        if (durMin > 12 * 60) { dnAlert.error("Invalid", "Shift duration cannot exceed 12 hours."); return; }
        if (durMin > 10 * 60) {
            const ok = await dnAlert.confirm({
                title: "Long shift",
                message: `Duration is ${fmtDuration(durMin)} - over 10 hours. Create anyway?`,
                confirmLabel: "Create",
            });
            if (!ok) return;
        }

        setSaving(true);
        try {
            const url = editingId ? `/api/shift/update/${editingId}` : `/api/shift/create`;
            const method = editingId ? "PUT" : "POST";
            const res = await apiFetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ shift_name: trimmed, start_time: start, end_time: end }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Save failed", data?.error || "Could not save shift.");
                return;
            }
            dnAlert.success(editingId ? "Updated" : "Created", `Shift "${trimmed}" saved.`);
            resetForm();
            fetchList();
        } catch (err) {
            console.error("save shift:", err);
            dnAlert.error("Error", "Network error.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        const ok = await dnAlert.confirm({
            title: "Delete shift?",
            message: `Delete "${row.shift_name}"? This cannot be undone.`,
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/shift/delete/${row.shift_id}`, {
                method: "DELETE",
                credentials: "include",
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Delete failed", data?.error || "Could not delete shift.");
                return;
            }
            dnAlert.success("Deleted", `Shift "${row.shift_name}" removed.`);
            fetchList();
        } catch (err) {
            console.error("delete shift:", err);
            dnAlert.error("Error", "Network error.");
        }
    };

    // Time picker - either 12h (H + M + AM/PM) or 24h (H + M).
    const TimePicker = ({ value, onChange, label }) => {
        const parts = is12h ? to12(value) : to24(value);
        const setH = (h) => onChange(is12h ? from12(h, parts.m, parts.ampm) : from24(h, parts.m));
        const setM = (m) => onChange(is12h ? from12(parts.h, m, parts.ampm) : from24(parts.h, m));
        const setAmPm = (ampm) => onChange(from12(parts.h, parts.m, ampm));
        return (
            <div className="sh-time-group">
                <label className="sh-label">{label}</label>
                <div className="shift-time-select">
                    <ScrollSelect
                        value={parts.h}
                        options={is12h ? HH12_OPTS : HH24_OPTS}
                        placeholder="HH"
                        onChange={setH}
                    />
                    <span className="shift-time-colon">:</span>
                    <ScrollSelect
                        value={parts.m}
                        options={MM_OPTS}
                        placeholder="MM"
                        onChange={setM}
                    />
                    {is12h && (
                        <ScrollSelect
                            value={parts.ampm}
                            options={AMPM_OPTS}
                            placeholder="AM"
                            onChange={setAmPm}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="sh-container">
            <div className={`sh-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                        </a>
                        Shifts
                    </h2>
                    <p className="dspr-subtitle">
                        Create work shifts with start and end times. The end time auto-suggests an 8-hour shift - edit it as needed. Shifts over 10 hours warn; over 12 hours are not allowed.
                    </p>
                </div>

                <div className="sh-card">
                    <div className="sh-fmt-row">
                        <span className="sh-fmt-label">Time format:</span>
                        <button type="button" className={`sh-fmt-btn ${is12h ? "active" : ""}`} onClick={() => setIs12h(true)}>12-hour (AM/PM)</button>
                        <button type="button" className={`sh-fmt-btn ${!is12h ? "active" : ""}`} onClick={() => setIs12h(false)}>24-hour</button>
                    </div>

                    <div className="sh-form-inline">
                        <div className="sh-field sh-field-name">
                            <label className="sh-label">Shift Name</label>
                            <input
                                className="sh-input"
                                type="text"
                                placeholder="e.g. General Shift"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={60}
                            />
                        </div>

                        <TimePicker value={start} onChange={handleStartChange} label="Start Time" />
                        <TimePicker value={end}   onChange={handleEndChange}   label="End Time" />

                        <div className="sh-field sh-duration">
                            <label className="sh-label">Duration</label>
                            <div className="sh-duration-body">
                                <span className={`sh-duration-value ${durMin > 12 * 60 ? "over" : durMin > 10 * 60 ? "warn" : ""}`}>
                                    {fmtDuration(durMin) || "-"}
                                </span>
                                {overnight && (
                                    <span className="sh-overnight-badge"><FaMoon /> Overnight</span>
                                )}
                            </div>
                        </div>

                        <div className="sh-field sh-field-actions">
                            <label className="sh-label sh-label-blank">&nbsp;</label>
                            <div className="sh-form-actions">
                                {editingId && (
                                    <button type="button" className="sh-btn sh-btn-cancel" onClick={resetForm}>
                                        Cancel
                                    </button>
                                )}
                                <button type="button" className="sh-btn sh-btn-primary" onClick={handleSave} disabled={saving}>
                                    {saving ? "Saving…" : editingId ? "Update Shift" : "Create Shift"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sh-card">
                    <div className="sh-table-wrap">
                        <table className="sh-table">
                            <thead>
                                <tr>
                                    <th>Shift Name</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>Duration</th>
                                    <th style={{ width: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={5} className="sh-empty">Loading…</td></tr>
                                ) : list.length === 0 ? (
                                    <tr><td colSpan={5} className="sh-empty">No shifts yet. Create one above.</td></tr>
                                ) : list.map(row => (
                                    <tr key={row.shift_id}>
                                        <td>
                                            <span className="sh-row-name">{row.shift_name}</span>
                                            {row.is_overnight === 1 && (
                                                <span className="sh-overnight-badge sh-inline"><FaMoon /> overnight</span>
                                            )}
                                        </td>
                                        <td>{fmtTime12(row.start_time)}</td>
                                        <td>{fmtTime12(row.end_time)}</td>
                                        <td>{fmtDuration(durationMins(row.start_time, row.end_time))}</td>
                                        <td>
                                            <button className="sh-icon-btn" title="Edit" onClick={() => loadIntoForm(row)}>
                                                <FaPenSquare />
                                            </button>
                                            <button className="sh-icon-btn sh-icon-btn-danger" title="Delete" onClick={() => handleDelete(row)}>
                                                <FaTrashAlt />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Shifts;
